import clsx from "clsx";
import { ReactNode } from "react";
import { Icon } from "../Icon";
import { Button } from "../Button";
import { Tag } from "../Tag";
import { ReportTypeLabelConfigType } from "react-migration/layouts/map/Multilayer/Bundle/Workbench/ManagmentTab/tabs/Reports";

export type UpgradeCTAProps = {
  /** Source URL for the image to be displayed */
  imageSrc?: string;
  /** Alt text for the image */
  imageAlt: string;
  /** Source URL for the logo to display in small size mode (optional; slot renders but is empty if not provided) */
  logoSrc?: string;
  /** Array of labels to display */
  labels?: ReportTypeLabelConfigType[];
  /** Title of the upgrade card */
  title: string;
  /** Description text */
  description: string;
  /** Array of list items to display as features */
  listItems?: string[];
  /** Custom element to render as the call-to-action */
  ctaElement: ReactNode;
  /** Optional className to apply to the container */
  className?: string;
  /** Optional boolean to apply border */
  variant?: "explore" | "assess";
  /** Layout size: "small" for compact mode (row title + optional logo, no image/list, minimal padding); "default" for full mode */
  size?: "small" | "default";
};

/**
 * Reusable component for displaying upgrade call-to-action cards
 */
export function UpgradeCTA({
  imageSrc,
  imageAlt,
  logoSrc,
  labels,
  title,
  description,
  listItems,
  ctaElement,
  className,
  variant = "explore",
  size = "default",
}: UpgradeCTAProps) {
  const isSmallMode = size === "small";

  const effectiveListItems = isSmallMode ? undefined : listItems;

  const outerClasses = clsx(
    "flex",
    "flex-col",
    isSmallMode ? "p-0 gap-0!" : "gap-4",
    variant !== "assess" && "p-3",
    variant === "explore" && "border",
    variant === "explore" && "border-neutral-300",
    "rounded-lg",
    "bg-background-surface",
    className
  );

  const innerClasses = clsx("flex flex-col gap-2 pb-2", {
    "p-4 pt-0": variant === "assess" && !isSmallMode,
    "p-0": isSmallMode,
  });

  return (
    <article className={outerClasses}>
      {!isSmallMode ? <img src={imageSrc} className="min-h-[120px]" alt={imageAlt} /> : null}
      <div className={innerClasses}>
        {isSmallMode ? (
          <div className="flex flex-row gap-2">
            <TitleComponent title={title} variant={variant} />
            {logoSrc ? (
              <div className="flex items-center justify-center h-full w-full max-w-[104px] max-h-[26px] px-3 py-1">
                <img src={logoSrc} alt={imageAlt} className="object-contain w-full h-full" />
              </div>
            ) : null}
          </div>
        ) : (
          <TitleComponent title={title} variant={variant} />
        )}
        {labels && (
          <div className="flex items-center justify-start gap-2">
            {labels.map((label) => (
              <Tag key={label.tag} tag={label.tag} color={label.color} />
            ))}
          </div>
        )}
        <p
          className={clsx(
            "leading-4 text-text-base-secondary-a font-normal m-0",
            variant === "explore" && "text-3xs",
            variant === "assess" && "text-xs"
          )}
        >
          {description}
        </p>
        {effectiveListItems ? (
          <ul className="flex flex-col gap-2 px-2 mt-4">
            {effectiveListItems.map((item, index) => (
              <ListItem key={index} variant={variant}>
                {item}
              </ListItem>
            ))}
          </ul>
        ) : null}
      </div>
      {ctaElement}
    </article>
  );
}

function ListItem({ children, variant }: { children: React.ReactNode; variant: "assess" | "explore" }) {
  return (
    <li className={clsx("flex", "items-center", "gap-2")}>
      <Icon icon="icon-lt-check-line" extraClasses="text-background-surface-success-accent" />
      <span
        className={clsx(
          "leading-4",
          "text-text-base-primary",
          "font-medium",
          variant === "explore" && "text-3xs",
          variant === "assess" && "text-md"
        )}
      >
        {children}
      </span>
    </li>
  );
}

type UpgradeButtonProps = {
  className?: string;
  onClick: () => void;
};

export function UpgradeButton({ className, onClick }: UpgradeButtonProps) {
  return (
    <Button size="md" className={className} onClick={onClick}>
      Upgrade <Icon icon="icon-lt-arrow-right-line" />
    </Button>
  );
}

const TitleComponent = ({ title, variant }: { title: string; variant: "explore" | "assess" }) => {
  return (
    <h1
      className={clsx(
        "font-bold text-text-base-primary",
        variant === "explore" && "text-sm",
        variant === "assess" && "text-lg"
      )}
    >
      {title}
    </h1>
  );
};
